import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'cityads-v3/types/reducer';
import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';
import config from 'cityads-v3/config';

import { SupportWidgetConfig, RedmineConfig } from './types';
import classes from './RedmineWidget.scss';

const { REDMINE_SCRIPT_URL } = config;
const REDMINE_CONTAINER_ID = 'helpdesk_widget';

function getRedmineDefaultConfig(t: (key: string) => string) {
  return {
    color: '#00A6EB',
    translation: {
      nameLabel: t('redmine.nameLabel'),
      emailLabel: t('redmine.emailLabel'),
      subjectLabel: t('redmine.subjectLabel'),
      descriptionLabel: t('redmine.descriptionLabel'),
      attachmentLinkLabel: t('redmine.attachmentLinkLabel'),
      createButtonLabel: t('redmine.createButtonLabel'),
      createSuccessLabel: t('redmine.createSuccessLabel'),
      createSuccessDescription: t('redmine.createSuccessDescription'),
      createErrorLabel: t('redmine.createErrorLabel'),
    },
  };
}

export default function RedmineWidget({ config }: SupportWidgetConfig<RedmineConfig>): React.ReactElement {
  const redmineNode = useRef<HTMLDivElement | null>(null);
  const scriptNode = useRef<HTMLScriptElement | null>(null);
  const containerNode = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation(['main']);

  const language = useSelector((state: RootState) => state.language.language);

  const configureWidget = useCallback(() => {
    const redmineWidget = window.RedmineHelpdeskWidget;

    if (!redmineWidget) { return; }

    const defaultConfig = getRedmineDefaultConfig(t);

    redmineWidget.config({
      ...defaultConfig,
      ...config,
      translation: {
        ...defaultConfig.translation,
        ...config?.translation,
      },
    });
  }, [config, t]);

  const addSubmitListener = useCallback((iframe: HTMLIFrameElement) => {
    iframe.contentWindow.addEventListener('submit', function (evt) {
      const form = evt.target as HTMLFormElement;
      const theme = form['issue[subject]'].value;

      amplitudeSendEvent('makeActions', 'd-v1-e39', 'help_button', 'question_ask', {
        question_topic: theme,
      });
    });
  }, []);

  const patchWidget = useCallback(() => {
    const redmineWidget = window.RedmineHelpdeskWidget;

    if (!redmineWidget) { return; }

    // Add form submit listener for current and future iframes
    const oldCreateIframeFunction = redmineWidget.create_iframe;
    redmineWidget.create_iframe = function () {
      oldCreateIframeFunction.apply(this, arguments);

      addSubmitListener(this.iframe);
    };
    addSubmitListener(document.querySelector(`#${REDMINE_CONTAINER_ID} iframe`));

    // Ensure widget button is stylized without repeated call to config
    const oldCreateWidgetFunction = redmineWidget.create_widget_button;
    redmineWidget.create_widget_button = function () {
      oldCreateWidgetFunction.apply(this, arguments);

      if (this.configuration['color']) {
        this.widget_button.style.backgroundColor = this.configuration['color'];
      }
    };

    // Ensure widget loader is stylized without repeated call to config
    const oldCreateLoadingDivFunction = redmineWidget.create_loading_div;
    redmineWidget.create_loading_div = function () {
      oldCreateLoadingDivFunction.apply(this, arguments);

      if (this.configuration['color']) {
        this.loading_div.style.borderTop = '8px solid ' + this.configuration['color'];
      }
    };

    // Ensure widget event listener fires just once
    redmineWidget.create_message_listener = function () {
      const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
      const eventer = window[eventMethod];
      const uneventer = eventMethod == 'attachEvent' ? window.detachEvent : window.removeEventListener;
      const messageEvent = eventMethod == 'attachEvent' ? 'onmessage' : 'message';

      if (this._windowEventListener) {
        uneventer(messageEvent, this._windowEventListener);
      }

      eventer(messageEvent, this._windowEventListener = (evt: MessageEvent) => {
        if (evt.source !== this.iframe.contentWindow) { return; }

        const data = JSON.parse(evt.data);

        if (data['reload'] == true)         { this.reload = true; }
        if (data['project_reload'] == true) { this.reload_project_data(); }
        if (data['arrange'] == true)        { this.arrange_iframe(); }
        if (data['close'] == true)          { this.toggle(); }
      }, false);
    };

    // Add iframe-local patches
    const oldAppendScripts = redmineWidget.append_scripts;
    redmineWidget.append_scripts = function () {
      oldAppendScripts.apply(this, arguments);

      // Prevent `details` button from showing up when there are no details
      const patchScript = document.createElement('script');
      patchScript.innerHTML = `
        document.querySelector('script[src$="iframe.js"]').onload = function () {
          var oldTicketErrors = window.ticketErrors;
          window.ticketErrors = function (errors) {
            oldTicketErrors(errors);
            if (!document.getElementById('ticket-errors').childElementCount) {
              document.getElementById('ticket-errors-link').style.display = 'none';
            }
          }
        };
      `;
      this.iframe.contentWindow.document.head.appendChild(patchScript);
    };
  }, [addSubmitListener]);

  const recreateWidget = useCallback(() => {
    const redmineWidget = window.RedmineHelpdeskWidget;

    if (!redmineWidget || !containerNode.current) { return; }

    containerNode.current.innerHTML = '';
    redmineWidget.loaded = false;
    redmineWidget.load();
  }, []);

  const onScriptLoad = useCallback(() => {
    patchWidget();
    configureWidget();
    recreateWidget();
  }, [patchWidget, configureWidget, recreateWidget]);

  const cleanupScript = useCallback(() => {
    if (window.RedmineHelpdeskWidget) {
      window.RedmineHelpdeskWidget = null;
    }
  }, []);

  useEffect(() => {
    const redmineScript = document.createElement('script');
    redmineScript.src = REDMINE_SCRIPT_URL;
    redmineScript.onload = onScriptLoad;

    const helpdeskWidget = document.createElement('div');
    helpdeskWidget.setAttribute('id', REDMINE_CONTAINER_ID);
    helpdeskWidget.classList.add(classes.redmineWidget);

    redmineNode.current.appendChild(helpdeskWidget);
    redmineNode.current.appendChild(redmineScript);

    containerNode.current = helpdeskWidget;
    scriptNode.current = redmineScript;

    return () => {
      redmineScript.onload = cleanupScript;
      redmineScript.src = null;

      if (redmineScript.parentNode) {
        redmineScript.parentNode.removeChild(redmineScript);
      }

      if (window.RedmineHelpdeskWidget) {
        window.RedmineHelpdeskWidget = null;
      }

      if (helpdeskWidget.parentNode) {
        helpdeskWidget.parentNode.removeChild(helpdeskWidget);
      }
    };
  }, []);

  useEffect(() => {
    configureWidget();
    recreateWidget();
  }, [configureWidget, language]);

  return (
    <>
      <Helmet>
        <body className={classes.bodyHasRedmineWidget} />
      </Helmet>
      <div ref={redmineNode}></div>
    </>
  );
}
